import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { Button } from "../primitives/Button";
import { SheetMenu, type SheetMenuItem } from "../primitives/SheetMenu";
import type { UiTheme } from "../theme";

// The bar under the wide grid while a block of cells is selected (#336 slice 6
// part 3, brief §7.3): in «Select» mode, or when the mouse or the keys have
// selected more than one cell. How many are selected (said by the grid), «Actions» (a menu of
// what the keys do: copy, paste, fill) and «Done». The grid's own twin of
// SelectionBar, which is the exercises'. 44pt targets: the buttons' height,
// plus a vertical hitSlop.
export function GridRangeBar({
  theme,
  count,
  items,
  menuOpen,
  setMenuOpen,
  onDone,
}: {
  theme: UiTheme;
  /** The cells in the block. */
  count: number;
  items: readonly SheetMenuItem[];
  /** The menu's state is the editing hook's: a context key opens it too. */
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  return (
    <View style={styles.bar}>
      {/* The count is spoken by the grid's own live region, once. With no cell
          anchored yet, a line says what to do (and why Actions is off). */}
      <Text style={styles.count}>
        {count === 0
          ? t("editorV3.grid.tapToStart", { defaultValue: "Tap a cell to start" })
          : t("editorV3.grid.cellsSelected", { count, defaultValue: "{{count}} cells selected" })}
      </Text>
      <Button
        theme={theme}
        variant="secondary"
        compact
        // Vertical only: sideways slop would reach the next button.
        hitSlop={{ top: 8, bottom: 8 }}
        label={t("editorV3.grid.menu.actions", { defaultValue: "Actions" })}
        onPress={() => setMenuOpen(true)}
        disabled={items.length === 0}
      />
      <Button
        theme={theme}
        variant="ghost"
        compact
        hitSlop={{ top: 8, bottom: 8 }}
        label={t("editorV3.selectDone", { defaultValue: "Done" })}
        onPress={onDone}
      />
      <SheetMenu
        theme={theme}
        visible={menuOpen}
        onClose={() => setMenuOpen(false)}
        title={t("editorV3.grid.menu.title", { defaultValue: "Selected cells" })}
        closeLabel={t("program.cancel", { defaultValue: "Cancel" })}
        items={items}
      />
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    bar: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap",
      gap: s.s2,
      paddingHorizontal: s.s3,
      paddingVertical: s.s2,
      borderWidth: 1,
      borderColor: p.border,
      borderRadius: r.r3,
      backgroundColor: p.surface,
    },
    count: { flex: 1, minWidth: 160, color: p.ink, fontSize: ty.sizes.sm, fontWeight: "700" },
  });
}
